// PageBoundary — contains a render error to the page CONTENT area. The app shell
// (sidebar, header, Dana, player) stays alive, the user sees what broke and can
// retry or go home, and navigating to another page clears the error automatically.
// Reusable everywhere a page renders (TenantLayout wraps every route with it);
// `RouteErrorFallback` is the react-router `errorElement` counterpart so the
// framework's raw "Unexpected Application Error!" screen is never shown.
import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { useLocation, useNavigate, useRouteError } from 'react-router-dom';
import { Button } from '@/features/shared/components/ui/button';
import { buildErrorReport, logErrorReport, type ClientErrorReport } from '@/lib/clientErrorReport';
import { ErrorReportDetails } from './ErrorReportDetails';

export function ErrorPanel({ title = 'This page hit a snag', message, report, onRetry, compact }: { title?: string; message?: string; report?: ClientErrorReport | null; onRetry?: () => void; compact?: boolean }) {
  const navigate = useNavigate();
  return (
    <div role="alert" className={`flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card text-center ${compact ? 'p-6' : 'py-16 px-6'}`}>
      <AlertTriangle className="h-8 w-8 text-red-700 dark:text-red-400" />
      <p className="font-medium text-charcoal">{title}</p>
      <p className="max-w-md text-sm text-muted-foreground">
        The rest of the app is still working. You can try again, or go back to the dashboard.
      </p>
      {report ? (
        <ErrorReportDetails report={report} />
      ) : message && (
        <code className="max-w-lg break-words rounded bg-muted px-2 py-1 text-xs text-muted-foreground">{message}</code>
      )}
      <div className="mt-1 flex gap-2">
        {onRetry && (
          <Button variant="outline" onClick={onRetry}>
            <RefreshCw className="mr-1.5 h-4 w-4" />
            Try again
          </Button>
        )}
        <Button variant="outline" onClick={() => navigate('..', { relative: 'path' })}>
          <Home className="mr-1.5 h-4 w-4" />
          Go to dashboard
        </Button>
      </div>
    </div>
  );
}

interface State {
  error: Error | null;
  atPath: string;
  report: ClientErrorReport | null;
}

class Boundary extends React.Component<{ children: React.ReactNode; path: string; title?: string; compact?: boolean }, State> {
  state: State = { error: null, atPath: '', report: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, report: buildErrorReport(error) };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const fresh = buildErrorReport(error, { componentStack: info?.componentStack });
    const prior = this.state.report;
    const report = prior ? { ...fresh, ref: prior.ref, at: prior.at, lastRequest: prior.lastRequest } : fresh;
    this.setState({ atPath: this.props.path, report });
    logErrorReport(report);
  }

  componentDidUpdate() {
    // Navigating away from the broken page is itself the recovery.
    if (this.state.error && this.state.atPath && this.props.path !== this.state.atPath) {
      this.setState({ error: null, atPath: '', report: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <ErrorPanel
          title={this.props.title}
          message={this.state.error.message || String(this.state.error)}
          report={this.state.report}
          compact={this.props.compact}
          onRetry={() => this.setState({ error: null, atPath: '', report: null })}
        />
      );
    }
    return this.props.children;
  }
}

/** Wrap a page (or a risky panel with `compact`) so its errors stay local. */
export const PageBoundary: React.FC<{ children: React.ReactNode; title?: string; compact?: boolean }> = ({ children, title, compact }) => {
  const { pathname } = useLocation();
  return (
    <Boundary path={pathname} title={title} compact={compact}>
      {children}
    </Boundary>
  );
};

/** react-router `errorElement`: replaces the framework's default crash screen. */
export const RouteErrorFallback: React.FC = () => {
  const err = useRouteError() as { message?: string; statusText?: string; status?: number } | undefined;
  const message = err?.message || err?.statusText || (err?.status ? `HTTP ${err.status}` : undefined);
  // One report per caught error: the reference on screen is the one in the log.
  // A thrown Error is reported as itself (its name decides whether its text is
  // shown); a route error response is reported by its status only.
  const [report] = React.useState(() => buildErrorReport(
    err instanceof Error
      ? err
      : { name: 'RouteErrorResponse', message: '', code: err?.status ? `HTTP_${err.status}` : undefined },
  ));
  React.useEffect(() => { logErrorReport(report); }, [report]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-lg">
        <ErrorPanel title="Something went wrong" message={message} report={report} onRetry={() => window.location.reload()} />
      </div>
    </div>
  );
};
